/**
 * Direct localStorage access for notes
 * Used by event handlers that run outside React context
 */

import type { NoteView } from '@/contexts/AppContext.js';

const NOTES_STORAGE_KEY = 'dexto-notes';

export function getNotes(): NoteView[] {
  try {
    const stored = localStorage.getItem(NOTES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    console.error('Failed to load notes from localStorage:', error);
  }
  return [];
}

export function saveNotes(notes: NoteView[]): void {
  try {
    localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(notes));
    // Dispatch custom event to notify React components
    window.dispatchEvent(new CustomEvent('notes-changed'));
  } catch (error) {
    console.error('Failed to save notes to localStorage:', error);
  }
}

export function createNoteInStorage(title?: string, content?: string): NoteView {
  const now = Date.now();
  const newNote: NoteView = {
    id: `${now}`,
    title: title || 'Untitled Note',
    content: content || '',
    createdAt: now,
    updatedAt: now,
  };

  const notes = getNotes();
  notes.unshift(newNote); // Add to beginning
  saveNotes(notes);

  return newNote;
}

export function deleteNoteFromStorage(id: string): void {
  const notes = getNotes();
  const filtered = notes.filter(note => note.id !== id);
  saveNotes(filtered);
}

export function updateNoteInStorage(id: string, updates: Partial<NoteView>): void {
  const notes = getNotes();
  const updated = notes.map(note =>
    note.id === id
      ? { ...note, ...updates, updatedAt: Date.now() }
      : note
  );
  saveNotes(updated);
}
